import { motion } from "framer-motion";
import { Eye, MessageSquareReply, BarChart3, Bell, Zap, Globe } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

export function UnifiedPlatformSection() {
  const { language, t: globalT } = useLanguage();

  const content = {
    fr: {
      badge: "Gestion Unifiée",
      title: "Un seul écran pour toutes vos plateformes",
      subtitle: "Lisez et répondez à tous vos avis Google, Facebook, Yelp, TripAdvisor et Trustpilot depuis une interface unique.",
      features: [
        {
          icon: Eye,
          title: "Lecture centralisée",
          description: "Tous vos avis de Google, Facebook, Yelp, TripAdvisor et Trustpilot remontent automatiquement. Plus besoin de jongler entre les plateformes.",
          color: "from-blue-500 to-blue-600",
          shadowColor: "shadow-blue-500/25",
        },
        {
          icon: MessageSquareReply,
          title: "Réponse multi-plateforme",
          description: "Répondez directement aux avis Google depuis OuraTrust. Votre réponse est publiée instantanément sur la plateforme d'origine.",
          color: "from-primary to-primary/80",
          shadowColor: "shadow-primary/25",
        },
        {
          icon: BarChart3,
          title: "Analytique unifiée",
          description: "Visualisez votre réputation globale avec des statistiques croisées : évolution de notes, volume d'avis par source, tendances hebdomadaires.",
          color: "from-success to-success/80",
          shadowColor: "shadow-success/25",
        },
        {
          icon: Bell,
          title: "Alertes intelligentes",
          description: "Recevez des notifications instantanées pour les avis négatifs sur toutes les plateformes. Ne ratez plus jamais une critique à gérer.",
          color: "from-warning to-warning/80",
          shadowColor: "shadow-warning/25",
        },
        {
          icon: Zap,
          title: "Réponses IA assistées",
          description: "Notre IA rédige des réponses personnalisées pour chaque avis. Chaque réponse nécessite votre validation explicite avant publication.",
          color: "from-accent to-accent/80",
          shadowColor: "shadow-accent/25",
        },
        {
          icon: Globe,
          title: "Intégrations natives",
          description: "Connectez votre fiche Google Business en un clic. Les avis Trustpilot et autres plateformes sont synchronisés automatiquement.",
          color: "from-slate-600 to-slate-700",
          shadowColor: "shadow-slate-500/25",
        },
      ],
    },
    en: {
      badge: "Unified Management",
      title: "One screen for all your platforms",
      subtitle: "Read and respond to all your Google, Facebook, Yelp, TripAdvisor, and Trustpilot reviews from a single interface.",
      features: [
        {
          icon: Eye,
          title: "Centralized Reading",
          description: "All your reviews from Google, Facebook, Yelp, TripAdvisor, and Trustpilot sync automatically. No more switching between platforms.",
          color: "from-blue-500 to-blue-600",
          shadowColor: "shadow-blue-500/25",
        },
        {
          icon: MessageSquareReply,
          title: "Multi-Platform Reply",
          description: "Reply directly to Google reviews from OuraTrust. Your response is published instantly on the original platform.",
          color: "from-primary to-primary/80",
          shadowColor: "shadow-primary/25",
        },
        {
          icon: BarChart3,
          title: "Unified Analytics",
          description: "Visualize your overall reputation with cross-platform stats: rating trends, review volume by source, weekly insights.",
          color: "from-success to-success/80",
          shadowColor: "shadow-success/25",
        },
        {
          icon: Bell,
          title: "Smart Alerts",
          description: "Get instant notifications for negative reviews across all platforms. Never miss a complaint that needs handling.",
          color: "from-warning to-warning/80",
          shadowColor: "shadow-warning/25",
        },
        {
          icon: Zap,
          title: "AI-Assisted Responses",
          description: "Our AI crafts personalized responses for every review. Every response requires your explicit approval before publication.",
          color: "from-accent to-accent/80",
          shadowColor: "shadow-accent/25",
        },
        {
          icon: Globe,
          title: "Native Integrations",
          description: "Connect your Google Business listing in one click. Trustpilot and other platforms sync automatically.",
          color: "from-slate-600 to-slate-700",
          shadowColor: "shadow-slate-500/25",
        },
      ],
    },
  };

  const t = content[language];

  return (
    <section className="py-28 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Globe size={16} />
            {t.badge}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {t.features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group relative bg-white rounded-2xl border border-slate-100 p-6 hover:border-primary/30 hover:shadow-xl transition-all duration-300"
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg ${feature.shadowColor} group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="text-white" size={26} />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-foreground mb-3">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.description}
              </p>

              {/* Subtle hover glow */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-[0.03] rounded-2xl transition-opacity duration-300`} />
            </motion.div>
          ))}
        </div>

        {/* Platform logos bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-sm text-muted-foreground mb-4">
            {globalT.partnerPlatforms}
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 opacity-70">
            <span className="text-lg font-semibold text-[#4285F4]">{globalT.platformGoogle}</span>
            <span className="text-lg font-semibold text-[#1877F2]">{globalT.platformFacebook}</span>
            <span className="text-lg font-semibold text-[#FF1A1A]">{globalT.platformYelp}</span>
            <span className="text-lg font-semibold text-[#34E0A1]">{globalT.platformTripAdvisor}</span>
            <span className="text-lg font-semibold text-[#00B67A]">{globalT.platformTrustpilot}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}